import type { FC } from "react";
import { useState } from "react";

import { MRTGrid } from "@foodpilot/foods";
import { useDataListsValuesOverridesContext } from "./DataListsValuesOverridesContext";
import { useTranslation } from "react-i18next";
import { Box, Stack, Dialog, Button, Typography } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import { useQueryClient } from "react-query";
import {
    DataListValueOverride,
    getAllDataListValueOverride,
} from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { DataListValueOverrideModal } from "./DataListValueOverrideModal";
import { useDeleteDataListValueOverride } from "src/api/client-api/dataListValueOverride/useDeleteDataListValueOverride";

export const DataListsValuesOverridesGrid: FC = () => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const queryClient = useQueryClient();

    const {
        overrides,
        isLoading,
        mrt_columns,
        totalItems,
        page,
        setPage,
        sorting,
        setSorting,
        delayedSearch,
        gridKey,
    } = useDataListsValuesOverridesContext();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<DataListValueOverride | undefined>(undefined);

    const handleDeleteClick = (id: number) => {
        setIdToDelete(id);
        setDisplayDialog(true);
    };

    const handleOpenEditModal = (row: DataListValueOverride) => {
        setEditing(row);
        setIsModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsModalOpen(false);
        setEditing(undefined);
    };

    const handleEditModalSuccess = () => {
        queryClient.invalidateQueries({ queryKey: [getAllDataListValueOverride] });
        setSnackbarProps({
            message: "Item updated successfully",
            type: "success",
        });
    };

    const deleteMutation = useDeleteDataListValueOverride();

    const handleDeleteConfirm = async () => {
        if (!idToDelete) return;

        deleteMutation.mutate(idToDelete, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [getAllDataListValueOverride],
                });
                setSnackbarProps({
                    message: "Item deleted successfully",
                    type: "success",
                });
                setDisplayDialog(false);
                setIdToDelete(undefined);
            },
            onError: () => {
                setSnackbarProps({
                    message: "Failed to delete item. Please try again.",
                    type: "error",
                });
            },
        });
    };

    return (
        <>
            <MRTGrid
                data={overrides || []}
                columns={mrt_columns.mrt_columns}
                enableRowActions
                enableRowSelection={false}
                enablePagination={true}
                enableSorting={false}
                enableSelectAll={false}
                enableColumnPinning={true}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        size: 80,
                        grow: false,
                    },
                }}
                renderRowActions={({ row }) => (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box
                            component="button"
                            onClick={() => handleOpenEditModal(row.original)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                                "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                },
                            }}
                        >
                            <FoodsIcon icon="edit" size={1} />
                        </Box>
                        <Box
                            component="button"
                            onClick={() => handleDeleteClick(row.original.id)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                                "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                },
                            }}
                        >
                            <FoodsIcon icon="delete" size={1} />
                        </Box>
                    </Stack>
                )}
                state={{
                    isLoading: isLoading,
                    pagination: page,
                    sorting: sorting,
                    globalFilter: delayedSearch,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    columnSizing: mrt_columns.columnSizing,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                // Server-side pagination
                rowCount={totalItems}
                manualPagination={true}
                onPaginationChange={setPage}
                manualSorting={true}
                onSortingChange={setSorting}
                manualFiltering={true}
                localization={{
                    actions: "",
                }}
                muiTableBodyRowProps={{
                    sx: {
                        "&:hover": {
                            backgroundColor: "#F9F9F8",
                        },
                        "& .MuiTableCell-root": {
                            paddingTop: "16px",
                            paddingBottom: "16px",
                        },
                    },
                }}
            />

            {/* Edit Modal */}
            <DataListValueOverrideModal
                open={isModalOpen}
                onClose={handleCloseEditModal}
                override={editing}
                onSuccess={handleEditModalSuccess}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={displayDialog}
                onClose={() => setDisplayDialog(false)}
                PaperProps={{
                    sx: { borderRadius: "16px", padding: "32px", width: "400px" },
                }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", mb: 2 }}>
                        {t("settings.score.entity_target_score_assignments.delete.confirm.title", "Confirm Deletion")}
                    </Typography>
                    <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#393A36", mb: 3 }}>
                        {t(
                            "settings.score.entity_target_score_assignments.delete.confirm.message",
                            "Are you sure you want to delete this item?",
                        )}
                    </Typography>
                    <Box sx={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                        <Button
                            variant="outlined"
                            onClick={() => setDisplayDialog(false)}
                            sx={{
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                borderColor: "#E7E6E4",
                                color: "#393A36",
                            }}
                        >
                            {t("cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDeleteConfirm}
                            sx={{
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                background: "linear-gradient(180deg, #FF6B6B 0%, #FF5252 100%)",
                                color: "white",
                            }}
                        >
                            {t("delete", "Delete")}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};
